import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import InvestmentStatusBadge from './InvestmentStatusBadge';
import FundingProgressBar from './FundingProgressBar';
import { ChevronRightIcon } from '../ui/icons';
import { formatCurrency } from '../../lib/formatPlan';

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

/**
 * Card-based, progressive-disclosure investment row — replaces the dense
 * table on the browsing/list screens (admin global list, investor "My
 * investments") per the design system's Robinhood-style pattern. The card
 * face only ever shows the 3-5 headline fields (title, principal, status,
 * funding progress); tapping it expands inline to reveal funded/remaining
 * detail and a lazy-loaded preview of recent payments/payouts — a full
 * dedicated detail page (`detailPath`) still exists for actual actions
 * (recording a payment, closing an investment), reached via the explicit
 * "View full details" link inside the expanded state.
 *
 * `usePaymentsQuery`/`usePayoutsQuery` are the admin or investor RTK Query
 * hooks, injected by the caller (same pattern as ReportsScreen) so this
 * one component works for both sides without duplicating it — both hook
 * families return the identical `{ payments/payouts, meta, parseError }`
 * shape, just scoped differently server-side. Both queries are skipped
 * until the card is actually expanded, so browsing the list never fires
 * N+1 requests up front.
 */
export default function InvestmentCard({ investment, title, subtitle, detailPath, usePaymentsQuery, usePayoutsQuery }) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const { data: paymentsData, isLoading: loadingPayments } = usePaymentsQuery(
    { investmentId: investment.id, per_page: 3 },
    { skip: !expanded }
  );
  const { data: payoutsData, isLoading: loadingPayouts } = usePayoutsQuery(
    { investmentId: investment.id, per_page: 3 },
    { skip: !expanded }
  );

  const payments = paymentsData?.payments ?? [];
  const payouts = payoutsData?.payouts ?? [];

  return (
    <div className="glass-card glass-card-hover overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className="w-full text-left p-4 sm:p-5 flex flex-col gap-3"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <h3 className="text-sm font-semibold text-foreground leading-snug truncate">{title}</h3>
              {investment.payoutSummary?.hasPendingPayout && (
                <span
                  className="h-2 w-2 rounded-full bg-warning shrink-0"
                  title="Payout pending — behind schedule"
                />
              )}
            </div>
            {subtitle && <p className="text-xs text-foreground-subtle mt-0.5 truncate">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <InvestmentStatusBadge status={investment.status} />
            <ChevronRightIcon className={clsx('h-4 w-4 text-foreground-subtle transition-transform duration-200', expanded && 'rotate-90')} />
          </div>
        </div>

        <p className="text-lg font-bold text-foreground tabular-nums">{formatCurrency(investment.principal)}</p>

        {investment.status === 'pending_funding' && (
          <FundingProgressBar fundedMinor={investment.fundedAmountMinor} principalMinor={investment.principalMinor} />
        )}
      </button>

      {expanded && (
        <div className="border-t border-border bg-background-elevated/60 p-4 sm:p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-foreground-subtle">Funded</p>
              <p className="text-foreground font-medium mt-0.5">{formatCurrency(investment.fundedAmount)}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-subtle">Remaining</p>
              <p className="text-foreground font-medium mt-0.5">{formatCurrency(investment.remainingAmount)}</p>
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-foreground-subtle mb-2">Recent payments</p>
            {loadingPayments && <p className="text-xs text-foreground-subtle">Loading…</p>}
            {!loadingPayments && payments.length === 0 && <p className="text-xs text-foreground-muted">No payments yet.</p>}
            {!loadingPayments && payments.length > 0 && (
              <ul className="space-y-1.5">
                {payments.map((p) => (
                  <li key={p.id} className="flex items-center justify-between text-xs">
                    <span className="text-foreground-muted">{p.method || 'payment'} · {formatDate(p.createdAt)}</span>
                    <span className="text-foreground font-medium tabular-nums">{formatCurrency(p.amount)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <p className="text-xs font-medium text-foreground-subtle mb-2">Recent payouts</p>
            {loadingPayouts && <p className="text-xs text-foreground-subtle">Loading…</p>}
            {!loadingPayouts && payouts.length === 0 && <p className="text-xs text-foreground-muted">No payouts yet.</p>}
            {!loadingPayouts && payouts.length > 0 && (
              <ul className="space-y-1.5">
                {payouts.map((p) => {
                  // Trust the stored `type` directly — never infer it from
                  // which of principal/interest is non-zero.
                  const amount = p.type === 'principal' ? p.principal : p.type === 'interest' ? p.interest : null;
                  return (
                    <li key={p.id} className="flex items-center justify-between text-xs">
                      <span className="text-foreground-muted">
                        {p.type === 'principal' ? 'Principal' : p.type === 'interest' ? 'Interest' : 'Payout'} · {formatDate(p.paidAt)}
                      </span>
                      <span className="text-foreground font-medium tabular-nums">{amount != null ? formatCurrency(amount) : '—'}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <button
            type="button"
            onClick={() => navigate(detailPath)}
            className="text-sm font-medium text-accent hover:underline"
          >
            View full details →
          </button>
        </div>
      )}
    </div>
  );
}
